import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  User,
  Download,
  Lock,
  Plus,
  Globe,
  MessageSquare,
  ListChecks,
} from 'lucide-react';
import { Button } from '../../components/shared';
import { DatePicker } from '../../components/client';
import DataTable from '../../components/shared/DataTable';
import { useVendorBookings } from '../../services/vendorBookingService';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { paymentVendorShareDisplay } from '../../utils/formatMoney';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const isPrivateBooking = (booking) => booking.booking_source === 'vendor_manual';

const getClientDisplayName = (booking) => {
  if (isPrivateBooking(booking)) {
    return booking.guest_name || booking.manual_title || 'Private client';
  }
  return (
    booking.client_display_name ||
    booking.client?.name ||
    'Unknown Client'
  );
};

const SOURCE_TABS = [
  { key: 'platform', label: 'Platform bookings', icon: Globe },
  { key: 'private', label: 'Private bookings', icon: Lock },
];

const VendorBookings = () => {
  const navigate = useNavigate();
  const [sourceTab, setSourceTab] = useState('platform');
  const [filters, setFilters] = useState({
    status: '',
    start_date: '',
    end_date: '',
    service_id: '',
    client_search: '' // Changed from client_id to client_search for text search
  });

  // Clean filters - remove empty values to avoid API errors
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
  );

  const apiFilters = {
    ...cleanFilters,
    booking_source: sourceTab === 'private' ? 'vendor_manual' : 'platform',
    per_page: 50,
  };

  // Fetch bookings with filters
  const { data: bookingsResponse, isLoading, error } = useVendorBookings(apiFilters);
  const stats = bookingsResponse?.statistics || {};
  const bookings = bookingsResponse?.data?.data || [];
  const tabTotal = bookingsResponse?.data?.total ?? bookings.length;
  const isPrivateTab = sourceTab === 'private';

  // Define table columns - simplified for overview
  const columns = useMemo(() => [
    {
      key: 'booking_reference',
      label: 'Booking #',
      sortable: true,
      width: 'w-56',
      render: (booking) => (
        <div className="flex items-center gap-2">
          <span className={`font-mono text-sm font-medium ${isPrivateTab ? 'text-gray-800 dark:text-gray-200' : 'text-primary-600 dark:text-primary-400'}`}>
            #{booking.booking_reference}
          </span>
          {isPrivateTab && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">
              <Lock className="w-2.5 h-2.5" />
              Private
            </span>
          )}
          {(Number(booking.unread_chat_count) || 0) > 0 && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-600 text-white">
              <MessageSquare className="w-2.5 h-2.5" />
              {Number(booking.unread_chat_count) > 99 ? '99+' : Number(booking.unread_chat_count)}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'client',
      label: isPrivateTab ? 'Guest client' : 'Client',
      sortable: false,
      width: 'w-56',
      render: (booking) => {
        const isPrivate = isPrivateTab || isPrivateBooking(booking);
        const displayName = getClientDisplayName(booking);
        const subline = isPrivate
          ? booking.guest_email || booking.guest_phone || 'Off-platform booking'
          : booking.client?.phone;

        return (
          <div className="flex items-center space-x-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                isPrivate ? 'bg-gray-100 dark:bg-gray-800 ring-2 ring-gray-200 dark:ring-gray-700' : 'bg-primary-100 dark:bg-primary-950/40'
              }`}
            >
              {isPrivate ? (
                <Lock className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              ) : (
                <User className="w-4 h-4 text-primary-600" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="font-medium text-gray-900 truncate">{displayName}</span>
              </div>
              <div className={`text-sm truncate ${isPrivate ? 'text-gray-500 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {subline}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'service',
      label: 'Service',
      sortable: false,
      width: 'w-56',
      render: (booking) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate">
            {booking.custom_service_name || booking.vendor_service?.service_name || '—'}
          </div>
          <div className="text-sm text-gray-500 truncate">
            {booking.vendor_service?.service_category?.category_name || (booking.custom_service_name ? 'Custom' : '')}
          </div>
        </div>
      )
    },
    {
      key: 'event_date',
      label: 'Event Date',
      sortable: true,
      width: 'w-40',
      type: 'date',
      render: (booking) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(booking.event_date, 'medium')}
            </div>
            <div className="text-xs text-gray-500">
              {booking.start_time ? formatTime(booking.start_time) : 'TBD'}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'vendor_payout_amount',
      label: 'Amount',
      sortable: true,
      width: 'w-32',
      type: 'currency',
      render: (booking) => {
        // Helper function to safely format currency
        const formatCurrency = (value) => {
          const num = parseFloat(value);
          if (isNaN(num) || num === null || num === undefined) {
            return '0.00';
          }
          return num.toFixed(2);
        };

        // Calculate service amount received by vendor from completed payments (USD display)
        const completedPayments = booking.payments?.filter(p => p.status === 'completed') || [];
        const totalServiceAmountReceived = completedPayments.reduce((sum, payment) => {
          return sum + paymentVendorShareDisplay(payment);
        }, 0);

        // Calculate deposit service amount (for deposit payments only)
        const depositServiceAmount = completedPayments
          .filter(p => p.payment_type === 'deposit')
          .reduce((sum, payment) => sum + paymentVendorShareDisplay(payment), 0);

        // Check if only deposit is paid (has deposit payment but not fully paid)
        const hasDepositPayment = completedPayments.some(p => p.payment_type === 'deposit');
        const hasFullPayment = completedPayments.some(p => p.payment_type === 'full_payment');
        const totalDue = parseFloat(booking.subtotal_amount || 0) + parseFloat(booking.platform_fee || 0);
        const isFullyPaid = parseFloat(booking.amount_paid || 0) >= totalDue && totalDue > 0;
        
        // If only deposit is paid, show deposit service amount, otherwise show total received or full payout
        const showDepositAmount = hasDepositPayment && !hasFullPayment && !isFullyPaid;
        const amountToDisplay = showDepositAmount 
          ? depositServiceAmount 
          : (completedPayments.length > 0 
              ? totalServiceAmountReceived 
              : parseFloat(booking.vendor_payout_amount || booking.subtotal_amount || 0));
        
        return (
          <div className="font-medium text-gray-900 text-right">
            ${formatCurrency(amountToDisplay)}
            {showDepositAmount && (
              <div className="text-xs text-gray-500 mt-0.5">Deposit</div>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-36',
      type: 'status',
      render: (booking) => {
        const statusConfig = {
          pending: {
            color:
              'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/50',
            icon: Clock,
          },
          confirmed: {
            color:
              'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50',
            icon: CheckCircle,
          },
          in_progress: {
            color:
              'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50',
            icon: Clock,
          },
          completed: {
            color:
              'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
            icon: CheckCircle,
          },
          cancelled: {
            color:
              'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800/50',
            icon: XCircle,
          },
          no_show: {
            color:
              'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
            icon: XCircle,
          },
        };

        const config = statusConfig[booking.status] || statusConfig.pending;
        const Icon = config.icon;

        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color} whitespace-nowrap`}
          >
            <Icon className="w-3 h-3 mr-1 flex-shrink-0" />
            {booking.status?.replace('_', ' ')}
          </span>
        );
      }
    }
  ], [isPrivateTab]);

  // Define table actions
  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      variant: 'default',
      onClick: (booking) => {
        navigate(`/vendor/bookings/${booking.id}`);
      }
    }
  ];

  // Handle status tab / filter change
  const handleStatusTab = (status) => {
    setFilters(prev => ({ ...prev, status }));
  };

  // Handle date range filter
  const handleDateFilter = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: '',
      service_id: '',
      client_search: ''
    });
  };

  // Custom search function
  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(booking => 
      booking.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.guest_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.guest_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.manual_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.vendor_service?.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.custom_service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.custom_package_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.event_location?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Export bookings
  const handleExport = () => {
    console.log('Exporting bookings...');
    // TODO: Implement CSV/Excel export
  };

  if (error) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error loading bookings</h3>
          <p className="text-red-600 dark:text-red-300 text-sm">Failed to load bookings. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Bookings"
        subtitle={
          isPrivateTab
            ? 'Private jobs you log yourself — separate from marketplace bookings and payouts.'
            : 'Manage platform booking requests from clients who book through VendaList.'
        }
        actions={
          <>
            {isPrivateTab && (
              <Button className="!rounded-xl" onClick={() => navigate('/vendor/private-bookings')}>
                <Plus className="w-4 h-4 mr-1" />
                New private booking
              </Button>
            )}
            <Button variant="outline" className="!rounded-xl" icon={Download} onClick={handleExport}>
              Export
            </Button>
          </>
        }
      />

      <VendorGuideCallout
        title={
          isPrivateTab
            ? VENDOR_PAGE_GUIDES.privateBookings.title
            : VENDOR_PAGE_GUIDES.bookings.title
        }
        steps={
          isPrivateTab
            ? VENDOR_PAGE_GUIDES.privateBookings.steps
            : VENDOR_PAGE_GUIDES.bookings.steps
        }
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
            aria-label="Booking source"
          >
            {SOURCE_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = sourceTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setSourceTab(tab.key)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {isActive && (
                    <span className="text-xs text-gray-400 font-normal tabular-nums">({tabTotal})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-5 space-y-5">
          <div className="flex flex-wrap gap-2">
            {[
              { value: '', label: 'All', count: stats.total_bookings || 0 },
              { value: 'pending', label: 'Pending', count: stats.pending_bookings || 0 },
              { value: 'confirmed', label: 'Confirmed', count: stats.confirmed_bookings || 0 },
              { value: 'in_progress', label: 'In progress', count: stats.in_progress_bookings || 0 },
              { value: 'completed', label: 'Completed', count: stats.completed_bookings || 0 },
              { value: 'cancelled', label: 'Cancelled', count: stats.cancelled_bookings || 0 },
              { value: 'no_show', label: 'No show', count: stats.no_show_bookings || 0 },
            ].map((tab) => (
              <button
                key={tab.value || 'all'}
                type="button"
                onClick={() => handleStatusTab(tab.value)}
                className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-colors border ${
                  filters.status === tab.value
                    ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
                    : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {tab.label}{' '}
                <span className="tabular-nums opacity-70">({tab.count})</span>
              </button>
            ))}
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Filters</h3>
              <Button variant="outline" size="sm" className="!rounded-lg" onClick={clearFilters}>
                Clear all
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                  Start date
                </label>
                <DatePicker
                  value={filters.start_date}
                  onChange={(value) => handleDateFilter('start_date', value)}
                  placeholder="Select start date"
                  className="w-full text-xs sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                  End date
                </label>
                <DatePicker
                  value={filters.end_date}
                  onChange={(value) => handleDateFilter('end_date', value)}
                  placeholder="Select end date"
                  className="w-full text-xs sm:text-sm"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                  {isPrivateTab ? 'Guest client' : 'Client'}
                </label>
                <input
                  type="text"
                  placeholder={
                    isPrivateTab
                      ? 'Search by guest name or email…'
                      : 'Search by client name…'
                  }
                  value={filters.client_search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, client_search: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          <DataTable
            data={bookings}
          columns={columns}
          actions={actions}
          loading={isLoading}
          searchable={true}
          filterable={false}
          sortable={true}
          selectable={true}
          onSearch={handleSearch}
          searchPlaceholder={
            isPrivateTab
              ? 'Search private bookings by reference, guest, or service...'
              : 'Search bookings by reference, client, service, or location...'
          }
          emptyMessage={
            isPrivateTab
              ? 'No private bookings yet. Create one for off-platform clients.'
              : 'No platform bookings found.'
          }
          pageSize={15}
          showPagination={true}
          className="!border-0 !shadow-none"
        />
        </div>
      </div>
    </div>
  );
};

export default VendorBookings;
